import { PaginationWithNavStaticHtmlModel } from '@_types/types';
import Icon from '@atoms/icons/Icon';
import { conditionClasses, getModifiers } from '@helpers/functions';
import { Fragment } from 'react/jsx-runtime';

export const PaginationWithNavStaticHtml = (model: PaginationWithNavStaticHtmlModel) => {
  const {
    disablePrevNav,
    disableNextNav,
    prevNavUrl,
    nextNavUrl,
    nodes,
    currentPage,
    numberOfPages,
    prevNavAriaLabel = 'Previous page',
    nextNavAriaLabel = 'Next page',
    nodeAriaLabel = 'Go to page',
  } = model;
  const styleModifier = getModifiers(model, 'kf-m-pagination-with-nav');

  const renderNode = (node: PaginationWithNavStaticHtmlModel['nodes'][0], index: number, array: PaginationWithNavStaticHtmlModel['nodes']) => {
    const pageNumber = node.pageNumber;
    const isCurrentPage = currentPage === pageNumber;
    const isLastPage = numberOfPages === pageNumber;
    const isFirstPage = pageNumber === 1;
    const isSecondLastPage = numberOfPages - 1 === pageNumber;
    const isFirstNode = index === 0;
    const isLastNode = index === array.length - 1;
    const currentPageIsFirstPage = currentPage === 1;
    const currentPageIsLastPage = currentPage === numberOfPages;

    const shouldNodeHiddenOnXs = () =>
      (currentPageIsFirstPage && pageNumber >= 3 && !isLastPage && numberOfPages > 4) ||
      (currentPageIsLastPage && pageNumber <= numberOfPages - 2 && !isFirstPage && numberOfPages > 4) ||
      (!currentPageIsFirstPage && !currentPageIsLastPage && pageNumber !== currentPage && !isFirstPage && !isLastPage && numberOfPages > 4);

    const shouldSeparatorHiddenOnXs = () => isCurrentPage && !isFirstPage && !isSecondLastPage && !isLastPage && numberOfPages > 4;
    const shouldSeparatorHiddenOnMAndAbove = () =>
      isCurrentPage && !isFirstPage && !isSecondLastPage && !isLastPage && numberOfPages > 6 && currentPage >= numberOfPages - 1;

    const shouldFrontDotsHiddenOnXs = () => {
      const hasMultipleNodes = array.length > 1;
      const secondNodePageNumber = array[1]?.pageNumber;

      if (isFirstNode && numberOfPages >= 5 && currentPage >= 3) {
        return false;
      }

      return !(isFirstNode && hasMultipleNodes && secondNodePageNumber > 2 && currentPage > 2) || numberOfPages <= 4;
    };

    const shouldFrontDotsHiddenOnMAndAbove = () => {
      const hasMultipleNodes = array.length > 1;
      const secondNodePageNumber = array[1]?.pageNumber;

      return !(isFirstNode && hasMultipleNodes && secondNodePageNumber > 2) || currentPage <= 4 || numberOfPages <= 6;
    };

    const shouldRearDotsHiddenOnXs = () => {
      const hasMultipleNodes = array.length > 1;
      const secondLastNodePageNumber = array[array.length - 2]?.pageNumber;

      if (isLastNode && numberOfPages >= 5 && currentPage <= numberOfPages - 2) {
        return false;
      }

      return (
        !(isLastNode && hasMultipleNodes && secondLastNodePageNumber < numberOfPages - 1 && currentPage < numberOfPages - 1) || numberOfPages <= 4
      );
    };

    const shouldRearDotsHiddenOnMAndAbove = () => {
      const hasMultipleNodes = array.length > 1;
      const secondLastNodePageNumber = array[array.length - 2]?.pageNumber;

      return (
        !(isLastNode && hasMultipleNodes && secondLastNodePageNumber < numberOfPages - 1) || numberOfPages <= 6 || currentPage >= numberOfPages - 2
      );
    };

    const className = conditionClasses({
      'kf-m-pagination__page': true,
      'kf-m-pagination__page--current': isCurrentPage,
      'last-page': isLastPage,
      'separator-hidden-on-xs': shouldSeparatorHiddenOnXs(),
      'separator-hidden-on-m-and-above': shouldSeparatorHiddenOnMAndAbove(),
      'node-hidden-on-xs': shouldNodeHiddenOnXs(),
    });

    const frontDotsClassName = conditionClasses({
      'kf-m-pagination__dots': true,
      'front-dots': true,
      'dots-hidden-on-xs': shouldFrontDotsHiddenOnXs(),
      'dots-hidden-on-m-and-above': shouldFrontDotsHiddenOnMAndAbove(),
    });

    const rearDotsClassName = conditionClasses({
      'kf-m-pagination__dots': true,
      'rear-dots': true,
      'dots-hidden-on-xs': shouldRearDotsHiddenOnXs(),
      'dots-hidden-on-m-and-above': shouldRearDotsHiddenOnMAndAbove(),
    });

    return (
      <Fragment key={index}>
        <span className={rearDotsClassName}>...</span>
        <a
          key={index}
          data-is-pagination-node
          aria-label={nodeAriaLabel + ' ' + node.pageNumber}
          className={className}
          data-page-number={node.pageNumber}
          href={node.url}
        >
          {node.pageNumber}
        </a>
        <span className={frontDotsClassName}>...</span>
      </Fragment>
    );
  };

  return (
    <div className={styleModifier}>
      <a
        aria-label={prevNavAriaLabel}
        className={`kf-m-pagination-with-nav__navigation-button-prev ${disablePrevNav ? 'disabled' : ''}`}
        data-is-pagination-node={disablePrevNav ? undefined : ''}
        data-page-number={disablePrevNav ? undefined : currentPage - 1}
        href={prevNavUrl}
      >
        <Icon iconPath="#kf-arrow-backward" viewBoxHeight={20} viewBoxWidth={20} />
      </a>
      <div className="kf-m-pagination-with-nav__nodes-container">
        {nodes.map((node, index, array) => renderNode(node, index, array))}
      </div>
      <a
        aria-label={nextNavAriaLabel}
        className={`kf-m-pagination-with-nav__navigation-button-next  ${disableNextNav ? 'disabled' : ''}`}
        data-is-pagination-node={disableNextNav ? undefined : ''}
        data-page-number={disableNextNav ? undefined : currentPage + 1}
        href={nextNavUrl}
      >
        <Icon iconPath="#kf-arrow-forward" viewBoxHeight={20} viewBoxWidth={20} />
      </a>
    </div>
  );
};
